import "./style.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Checkbox, DetailedRadio, TextArea } from "@amzn/stencil-react-components/form";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { ApprovalsListForModal } from "common/components/Approvals/ApprovalsListForModal";
import { InlineUserReference } from "common/components/InlineUserReference";
import { CreateOrderRequestContent } from "common/types";
import { PreCheckoutInformation } from "common/types";
import { AcknowledgementAuditEntry } from "common/types/orders-types";
import { MessageStruct } from "common/types/ui-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserPermissions } from "lib/services";
import React, { FunctionComponent, useEffect, useState } from "react";

import { MessagesList } from "../MessagesList";
import { useSecurityOrderState } from "./security-form-input";
import { SecurityDataForm } from "./SecurityDataForm";

export interface SubmitSecurityOrderModalProps {
  name: string;
  taxonomyId: string;
  submitOrderHandler: (request: CreateOrderRequestContent) => void;
  preCheckoutInformation?: PreCheckoutInformation;
  close: () => void;
  csrfTokenIsLoading: boolean;
  isLoadingCreate: boolean;
  messages?: MessageStruct[];
}

export const SubmitSecurityOrderModal: FunctionComponent<SubmitSecurityOrderModalProps> = ({
  name,
  taxonomyId,
  preCheckoutInformation,
  submitOrderHandler,
  close,
  csrfTokenIsLoading,
  isLoadingCreate,
  messages,
}: SubmitSecurityOrderModalProps) => {
  const { inputs, updateInputs } = useSecurityOrderState();
  const userAlias = UserPermissions.current.username;
  const [acknowledgementAudit, setAcknowledgementAudit] = useState<AcknowledgementAuditEntry[]>([]);

  useEffect(() => {
    if (inputs.goNext) {
      updateInputs("showForm", false);
    } else {
      updateInputs("showForm", true);
    }
  }, [inputs.goNext]);

  useEffect(() => {
    updateInputs(
      "securitySummary",
      `I already have an approval, URL: <a target="_blank" href="${inputs.securityUrl}" style="color: #1768C9;">${inputs.securityUrl}</a>`
    );
  }, [inputs.securityUrl]);

  const submitOrder = async () => {
    const request: CreateOrderRequestContent = {
      items: {
        [taxonomyId!]: {
          quantity: 1,
          details: {
            softwareOrderDetails: { securitySummary: inputs.securitySummary },
          },
        },
      },
      comment: inputs.comment,
      acknowledgementAudit,
    };
    await submitOrderHandler(request);
  };

  const handleFormSave = (formData: string) => {
    updateInputs("securitySummary", formData);
  };

  const handleFormSubmit = (enableSubmit: boolean) => {
    updateInputs("formIsValid", enableSubmit);
  };

  const handleConfirmCheck = (acknowledged: boolean, policies: string[], input: string) => {
    updateInputs(input, acknowledged); // update use state variable
    for (let i = 0; i < policies.length; i++) {
      const policyName = policies[i];
      acknowledgementAudit.push({
        by: userAlias!,
        on: new Date(),
        policyName,
        acknowledged,
      });
    }
  };

  const inputsAreChecked = () => {
    return inputs.dataClassificationConfirmIsChecked && inputs.securityPoliciesConfirmIsChecked;
  };

  const canClickSubmmit = () => {
    const securityURLAdded = inputs.securityUrl.trim().length > 5;
    const canAutoApprove = preCheckoutInformation !== undefined && preCheckoutInformation!.canBeAutoApproved;
    const submitSecurityURL = !inputs.showForm && securityURLAdded;
    const submitSecurityForm = inputs.showForm && inputs.formIsValid;
    const hasSecurityData = submitSecurityURL || submitSecurityForm;

    if (!inputsAreChecked() || isLoadingCreate) return false;
    if (canAutoApprove && hasSecurityData) return true;
    return inputs.comment != undefined && inputs.comment.trim() && hasSecurityData;
  };

  const canClickNext = () => {
    const hasPreCheckoutData = preCheckoutInformation !== undefined;
    const canAutoApprove = hasPreCheckoutData && preCheckoutInformation!.canBeAutoApproved;
    const hasApproveJustification =
      hasPreCheckoutData && !canAutoApprove && inputs.comment != undefined && inputs.comment.trim();
    if (!inputsAreChecked() || isLoadingCreate || (!canAutoApprove && !hasApproveJustification)) return false;
    return true;
  };

  const selectApprovalRequestOption = (
    <Col>
      <Row>
        <Text>{getString("submitSecurityApprovalOrder.approvalSelectionText")}</Text>
      </Row>
      <Row gridGap="S300">
        <DetailedRadio
          data-testid="radio-prev-approval"
          titleText="Already have an approval link"
          checked={inputs.hasApproval}
          onChange={() => updateInputs("hasApproval", true)}
        />
        <DetailedRadio
          data-testid="radio-new-approval"
          titleText="Create an approval request"
          checked={!inputs.hasApproval}
          onChange={() => updateInputs("hasApproval", false)}
        />
      </Row>
    </Col>
  );

  const securityApprovalUrl = (
    <Col padding="L400">
      <Label htmlFor="approval-url-text-area">
        {getString("submitSecurityApprovalOrder.submitApplinkSecurityOrder.approvalURLDescriptionBody")}
      </Label>
      <TextArea
        id="approval-url-text-area"
        data-cy="approval-url-text-area"
        placeholder={
          getString("submitSecurityApprovalOrder.submitApplinkSecurityOrder.approvalURLDescriptionPlaceholder.part1") +
          name +
          getString("submitSecurityApprovalOrder.submitApplinkSecurityOrder.approvalURLDescriptionPlaceholder.part2")
        }
        value={inputs.securityUrl}
        onChange={(e) => updateInputs("securityUrl", e.target.value)}
      />
    </Col>
  );

  const modalButtons = (
    <div key="action-buttons">
      {inputs.hasApproval || (!inputs.hasApproval && inputs.showForm) ? (
        <Button
          key="submitOrder-submit"
          data-testid="submitOrder-submit"
          data-cy="submit-order-button"
          variant={ButtonVariant.Primary}
          disabled={!canClickSubmmit()}
          onClick={submitOrder}
        >
          Submit
          {isLoadingCreate ? <Spinner size={SpinnerSize.Small} fr={undefined} /> : null}
        </Button>
      ) : (
        <Button
          key="next-button"
          data-testid="next-button"
          data-cy="next-button"
          variant={ButtonVariant.Primary}
          disabled={!canClickNext()}
          onClick={() => updateInputs("goNext", false)}
        >
          Next
        </Button>
      )}
    </div>
  );

  const selectModalBodyComponent = () => {
    if (!preCheckoutInformation || csrfTokenIsLoading) {
      return (
        <>
          <Spinner size={SpinnerSize.Medium} fr={undefined} />
          <br />
        </>
      );
    }

    if (preCheckoutInformation.canBeAutoApproved) {
      return (
        <>
          <Text color="neutral90" fontSize="T300">
            {getString("submitSanctionOrder.autoApproveOrderBody")}
          </Text>
          <br />
        </>
      );
    }

    const requiredApprover = preCheckoutInformation.requiredSTPApprover;

    return (
      <>
        <ApprovalsListForModal requiredApprovals={preCheckoutInformation.requiredApprovals}></ApprovalsListForModal>

        {requiredApprover && (
          <Text color="neutral90" fontSize="T200">
            {getString("submitSanctionOrder.managerRequest.body")}
            <strong>
              {" "}
              <InlineUserReference userReference={requiredApprover}></InlineUserReference>{" "}
            </strong>
          </Text>
        )}
        <Label htmlFor="comment-text-area">{getString("submitSanctionOrder.managerRequest.textAreaLabel")}</Label>
        <TextArea
          id="comment-text-area"
          data-cy="comment-text-area"
          placeholder={getString("submitSanctionOrder.managerRequest.textAreaPlaceHolder")}
          value={inputs.comment}
          onChange={(e) => updateInputs("comment", e.target.value)}
          style={{ width: "100%" }}
        />
        <br />
      </>
    );
  };

  const modalBodyComponent = selectModalBodyComponent();

  const createModalContent = (
    <>
      <Col gridGap="S200" alignSelf="flex-start">
        {modalBodyComponent}
      </Col>
      <Col gridGap="S400" alignItems="start">
        <Col gridGap="S200" alignSelf="flex-start">
          <MessageBanner type={MessageBannerType.Informational}>
            {getString("submitSecurityApprovalOrder.submitApplinkSecurityOrder.dataClassificationInformation")}
          </MessageBanner>
        </Col>

        {/* Data Classification Acknowledgement */}
        <Col gridGap="S200">
          <Row gridGap="S200">
            <Checkbox
              id="checkReadSecurityData"
              data-cy="read-high-sensitive-security-data-policy-checkbox"
              checked={inputs.dataClassificationConfirmIsChecked}
              onChange={(e: any) =>
                handleConfirmCheck(
                  e.target.checked,
                  [getString("submitSecurityApprovalOrder.policy1.name")],
                  "dataClassificationConfirmIsChecked"
                )
              }
            />
            <Label htmlFor="checkReadSecurityData">
              {getString("submitSecurityApprovalOrder.acceptConditions.part1")}
              {getString("submitSecurityApprovalOrder.acceptConditions.part4")}
            </Label>
          </Row>
        </Col>

        {/* Acceptable Use Policy and Third Party Use Policy */}
        <Col gridGap="S200">
          <Row gridGap="S200">
            <Checkbox
              id="checkReadAcceptableUsePolicy"
              data-cy="read-acceptable-use-policy-checkbox"
              checked={inputs.securityPoliciesConfirmIsChecked}
              onChange={(e: any) =>
                handleConfirmCheck(
                  e.target.checked,
                  [
                    getString("submitSecurityApprovalOrder.policy2.name"),
                    getString("submitSecurityApprovalOrder.policy3.name"),
                  ],
                  "securityPoliciesConfirmIsChecked"
                )
              }
            />
            <Label htmlFor="checkReadAcceptableUsePolicy">
              {getString("submitSecurityApprovalOrder.acceptConditions.part2")}
              <Link href={getString("submitSecurityApprovalOrder.policy2.url")} target="_blank">
                {getString("submitSecurityApprovalOrder.policy2.name")}
              </Link>
              {getString("submitSecurityApprovalOrder.acceptConditions.part3")}
              <Link href={getString("submitSecurityApprovalOrder.policy3.url")} target="_blank">
                {getString("submitSecurityApprovalOrder.policy3.name")}
              </Link>
              {getString("submitSecurityApprovalOrder.acceptConditions.part4")}
            </Label>
          </Row>
        </Col>
      </Col>
      <br />
      <MessagesList messages={messages}></MessagesList>
      {selectApprovalRequestOption}
      <br />
      {inputs.hasApproval && securityApprovalUrl}
      <Col gridGap="S200" alignSelf="flex-start">
        <Text fontSize="T100" color="neutral70">
          {getString("submitSanctionOrder.requiredField")}
        </Text>
      </Col>
    </>
  );

  return (
    <ModalContent
      titleText={name ? name : ""}
      buttons={[
        <Button key="submitOrder-close" variant={ButtonVariant.Tertiary} onClick={close}>
          Cancel
        </Button>,
        modalButtons,
      ]}
      maxWidth={815}
    >
      {inputs.showForm ? (
        <SecurityDataForm
          onSave={handleFormSave}
          onValidationSubmit={handleFormSubmit}
          softwareName={name ? name : ""}
        />
      ) : (
        createModalContent
      )}
    </ModalContent>
  );
};
